// src/components/UserSettingsSidebar.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { supabase } from '../supabaseClient'; // Adjust the import as needed

import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
} from '@mui/material';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

// --- NEW: Define Default Prompt String (should match backend) --- 
const DEFAULT_DIRECT_SYSTEM_PROMPT = "You are a helpful AI assistant. Answer the user's question directly.";
// -------------------------------------------------------------

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Helper function to sort by label
const sortByLabel = (a, b) => (a.label || '').localeCompare(b.label || '');

// Options for temperature (remains the same)
const temperatureOptions = [
  { value: '0.0', label: '0.0' },
  { value: '0.1', label: '0.1' },
  { value: '0.2', label: '0.2' },
  { value: '0.3', label: '0.3' },
  { value: '0.4', label: '0.4' },
  { value: '0.5', label: '0.5' },
  { value: '0.6', label: '0.6' },
  { value: '0.7', label: '0.7' },
  { value: '0.8', label: '0.8' },
  { value: '0.9', label: '0.9' },
  { value: '1.0', label: '1.0' },
  { value: '1.1', label: '1.1' },
  { value: '1.2', label: '1.2' },
  { value: '1.3', label: '1.3' },
  { value: '1.4', label: '1.4' },
  { value: '1.5', label: '1.5' },
  { value: '1.6', label: '1.6' },
  { value: '1.7', label: '1.7' },
  { value: '1.8', label: '1.8' },
  { value: '1.9', label: '1.9' },
  { value: '2.0', label: '2.0' },
];

// Utility function to find the provider for a given model value (will be replaced by state-driven version below)
let findProviderForModel = () => null;

const handleDeleteChatHistoryExtra = async () => {
  try {
    // Get the current session to extract the token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session ? session.access_token : '';
    if (!token) {
      console.error('No token available. Please login.');
      return;
    }
    // Call the delete_chat_history endpoint (adjust URL as needed)
    const response = await fetch(`${BACKEND_URL}/delete_chat_history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ token: token })
    });
    if (response.ok) {
      console.log('Chat history deleted successfully via extra action.');
    } else {
      const errorText = await response.text();
      console.error('Error deleting chat history:', errorText);
    }
  } catch (error) {
    console.error('Error in handleDeleteChatHistoryExtra:', error);
  }
};

const UserSettingsSidebar = ({
  open,
  onClose,
  userId,
  activeSessionId,
  // Props kept for compatibility but cleaned up in render
}) => {
  // Log props on every render
  console.log(`[UserSettingsSidebar RENDER] Received props: open=${open}, userId=${userId}, activeSessionId=${activeSessionId}`);

  const [saveSuccess, setSaveSuccess] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // API Key management state
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreateKeyDialog, setShowCreateKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [showNewApiKey, setShowNewApiKey] = useState(false);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');

  const [settings, setSettings] = useState({
    embedding_model: 'bge-m3-embedding', // Re-added: user can now switch embedding models
    system_prompt: '',
    llm_model: 'gpt-4o',
    temperature: '0.0',
    rag_mode: 'similarity_filter',
    enable_reranker: true,
    direct_system_prompt: '',
    top_p: 1.0,
    total_chunks: 50,
    faq_direct_answer: false,
    answer_max_token: 512,
    top_k: 15,
    cosine_similarity_threshold: 0.3,
    enable_grounding_check: false,
    grounding_similarity_threshold: 0.4,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [systemDefaults, setSystemDefaults] = useState(null);

  // NEW state for provider selection and dynamic model catalog
  const [selectedProvider, setSelectedProvider] = useState('');
  const [llmModelsByProvider, setLlmModelsByProvider] = useState({});
  const [providerOptions, setProviderOptions] = useState([]);

  // Re-added: State for available embedding models
  const [embeddingModelOptions, setEmbeddingModelOptions] = useState([]);

  // State for Integrations expansion (Reverted)
  // const [integrationsOpen, setIntegrationsOpen] = useState(false);
  // const [chatwootIntegrationsOpen, setChatwootIntegrationsOpen] = useState(false);

  // Fetch system defaults from API on component mount
  useEffect(() => {
    const fetchSystemDefaults = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/system-defaults`);
        if (response.ok) {
          const data = await response.json();
          setSystemDefaults(data);
          console.log('[UserSettingsSidebar] Fetched system defaults:', data);
        } else {
          console.warn('[UserSettingsSidebar] Failed to fetch system defaults');
        }
      } catch (e) {
        console.error('[UserSettingsSidebar] Error fetching system defaults:', e);
      }
    };
    fetchSystemDefaults();
  }, []);

  // Fetch active LLM models from Supabase and group by provider
  useEffect(() => {
    const loadModels = async () => {
      try {
        const { data, error } = await supabase
          .from('llm_models')
          .select('provider, model_identifier, display_label, supports_vision, is_active')
          .eq('is_active', true);
        if (error) throw error;
        const grouped = {};
        (data || []).forEach((row) => {
          const provider = row.provider || 'Unknown';
          if (!grouped[provider]) grouped[provider] = [];
          grouped[provider].push({
            value: row.model_identifier,
            label: row.display_label || row.model_identifier,
            supportsVision: !!row.supports_vision,
          });
        });
        Object.keys(grouped).forEach((p) => grouped[p].sort(sortByLabel));
        const providers = Object.keys(grouped).sort();
        setLlmModelsByProvider(grouped);
        setProviderOptions(providers);
        if (!selectedProvider && providers.length > 0) {
          setSelectedProvider(providers[0]);
        }
      } catch (e) {
        console.error('Failed to load LLM models:', e);
      }
    };
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-added: Fetch active embedding models from Supabase
  useEffect(() => {
    const loadEmbeddingModels = async () => {
      try {
        const { data, error } = await supabase
          .from('embedding_models')
          .select('model_identifier, display_label, provider, embedding_dimension, is_active')
          .eq('is_active', true)
          .order('display_label');
        if (error) throw error;
        const options = (data || []).map((row) => ({
          value: row.model_identifier,
          label: `${row.display_label || row.model_identifier} (${row.provider}, ${row.embedding_dimension}D)`,
        }));
        setEmbeddingModelOptions(options);
        console.log('[UserSettingsSidebar] Loaded embedding models:', options);
      } catch (e) {
        console.error('Failed to load embedding models:', e);
      }
    };
    loadEmbeddingModels();
  }, []);

  // === Log Settings State on Change ===
  useEffect(() => {
    console.log("Settings state updated:", settings);
  }, [settings]);
  // ====================================

  // Memoize available models based on selected provider
  const availableModels = useMemo(() => {
    return selectedProvider ? (llmModelsByProvider[selectedProvider] || []) : [];
  }, [selectedProvider, llmModelsByProvider]);

  // Override provider finder now that state is available
  findProviderForModel = (modelValue) => {
    for (const provider in llmModelsByProvider) {
      if ((llmModelsByProvider[provider] || []).some((m) => m.value === modelValue)) {
        return provider;
      }
    }
    return null;
  };

  // When model catalog or saved llm_model changes, map provider without overriding the saved model
  useEffect(() => {
    if (!settings.llm_model) return;
    const p = findProviderForModel(settings.llm_model);
    if (p && p !== selectedProvider) {
      setSelectedProvider(p);
    }
  }, [settings.llm_model, llmModelsByProvider, selectedProvider]);

  useEffect(() => {
    // Log activeSessionId when it changes or when the component mounts/updates with it
    console.log(`[UserSettingsSidebar Effect - Prop Change] activeSessionId: ${activeSessionId}, userId: ${userId}`);

    async function fetchSettingsForSession() {
      // console.log(`[UserSettingsSidebar] fetchSettingsForSession triggered. userId: ${userId}, activeSessionId: ${activeSessionId}`);
      setLoading(true);
      setError('');
      let currentSettings = null; // To hold the final settings to be applied

      const { data: { session: authSession } } = await supabase.auth.getSession();
      const token = authSession ? authSession.access_token : '';

      if (!token && (activeSessionId || userId)) { // Token is needed if we are to fetch
        setError('Authentication token not found. Cannot fetch settings.');
        setLoading(false);
        // Apply application defaults if token is missing and we intended to fetch
        currentSettings = { /* Application Defaults */
          embedding_model: 'bge-m3-embedding', // Re-added: system default
          system_prompt: "You are an AI assistant that provides helpful and concise answers based solely on the provided document and conversation history. Use the documents and the conversation history to understand the user's question and provide a relevant and accurate answer. Do not include verbatim quotes or references to previous messages or questions in your answer. DO NOT MENTION ANY PREVIOUS CHAT IN YOUR ANSWER. Do not repeat any previous chat in your answer. DO NOT USE ANY EXTERNAL KNOWLEDGE OR YOUR PRETRAINED INFORMATION. DO NOT USE ANY INFORMATION NOT PRESENT IN THE DOCUMENT. Focus on providing concise, relevant, clear and accurate responses from the context. If you cannot find the answer in the context or conversation history, state that the information is not available and do not offer additional assistance or ask follow-up questions.",
          llm_model: process.env.REACT_APP_DEFAULT_LLM_MODEL || 'gemini-2.0-flash',
          temperature: 0.0, top_p: 1.0, total_chunks: 50, answer_max_token: 512, top_k: 15,
          cosine_similarity_threshold: 0.3, rag_mode: 'similarity_filter',
          enable_grounding_check: false, grounding_similarity_threshold: 0.4,
          enable_reranker: true, direct_system_prompt: '',
        };
        // Proceed to common processing with these defaults
      } else if (token) {
        // 1. Try fetching from session_settings if activeSessionId is available
        if (activeSessionId) {
          console.log(`[UserSettingsSidebar] Attempting to fetch settings for session_id: ${activeSessionId}`);
          const sessionSettingsUrl = `${(window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_URL) || process.env.REACT_APP_SUPABASE_URL}/rest/v1/session_settings?session_id=eq.${activeSessionId}&select=*`;
          try {
            const response = await fetch(sessionSettingsUrl, {
              headers: {
                apikey: (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY,
                Authorization: `Bearer ${token}`,
              },
            });
            if (response.ok) {
              const data = await response.json();
              if (data && data.length > 0) {
                currentSettings = data[0];
                delete currentSettings.session_id; // Not part of 'settings' state keys
                console.log("[UserSettingsSidebar] Successfully loaded settings from session_settings:", currentSettings);
              } else {
                console.log(`[UserSettingsSidebar] No specific settings found in session_settings for session ${activeSessionId}.`);
              }
            } else {
              console.warn(`[UserSettingsSidebar] Error fetching session_settings for ${activeSessionId} (status: ${response.status}): ${response.statusText}.`);
            }
          } catch (e) {
            console.warn(`[UserSettingsSidebar] Exception while fetching session_settings for ${activeSessionId}:`, e);
          }
        }

        // 2. If no session-specific settings found and userId is available, try fetching from user_settings
        if (!currentSettings && userId) {
          console.log(`[UserSettingsSidebar] Attempting to fetch user default settings for user_id: ${userId}`);
          const userSettingsUrl = `${(window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_URL) || process.env.REACT_APP_SUPABASE_URL}/rest/v1/user_settings?user_id=eq.${userId}&select=embedding_model,system_prompt,llm_model,temperature,top_p,total_chunks,answer_max_token,top_k,cosine_similarity_threshold,rag_mode,enable_grounding_check,grounding_similarity_threshold,enable_reranker,direct_system_prompt`;
          try {
            const response = await fetch(userSettingsUrl, {
              headers: {
                apikey: (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY,
                Authorization: `Bearer ${token}`,
              },
            });
            if (response.ok) {
              const data = await response.json();
              if (data && data.length > 0) {
                currentSettings = data[0];
                delete currentSettings.id; // Not part of 'settings' state keys
                delete currentSettings.user_id; // Not part of 'settings' state keys
                console.log("[UserSettingsSidebar] Successfully loaded settings from user_settings as fallback:", currentSettings);
              } else {
                console.log(`[UserSettingsSidebar] No user default settings found in user_settings for user ${userId}.`);
              }
            } else {
              console.warn(`[UserSettingsSidebar] Error fetching user_settings for ${userId} (status: ${response.status}): ${response.statusText}.`);
            }
          } catch (e) {
            console.warn(`[UserSettingsSidebar] Exception while fetching user_settings for ${userId}:`, e);
          }
        }
      } // End of token check block

      // 3. If still no settings loaded (or token was missing for fetch), use system defaults from database
      if (!currentSettings) {
        console.log("[UserSettingsSidebar] No session/user settings loaded or fetch failed. Using system defaults from database.");
        if (systemDefaults) {
          currentSettings = { ...systemDefaults };
          console.log("[UserSettingsSidebar] Applied system defaults from database:", currentSettings);
        } else {
          // Final fallback if system defaults not loaded yet
          console.warn("[UserSettingsSidebar] System defaults not loaded yet. Using hardcoded fallback.");
          currentSettings = {
            embedding_model: 'bge-m3-embedding', // Re-added: hardcoded fallback default
            system_prompt: "You are an AI assistant that provides helpful and concise answers based solely on the provided document and conversation history. Use the documents and the conversation history to understand the user's question and provide a relevant and accurate answer. Do not include verbatim quotes or references to previous messages or questions in your answer. DO NOT MENTION ANY PREVIOUS CHAT IN YOUR ANSWER. Do not repeat any previous chat in your answer. DO NOT USE ANY EXTERNAL KNOWLEDGE OR YOUR PRETRAINED INFORMATION. DO NOT USE ANY INFORMATION NOT PRESENT IN THE DOCUMENT. Focus on providing concise, relevant, clear and accurate responses from the context. If you cannot find the answer in the context or conversation history, state that the information is not available and do not offer additional assistance or ask follow-up questions.",
            llm_model: 'gpt-4o',
            temperature: 0.0,
            top_p: 1.0,
            total_chunks: 50,
            answer_max_token: 512,
            top_k: 15,
            cosine_similarity_threshold: 0.3,
            rag_mode: 'similarity_filter',
            enable_grounding_check: false,
            grounding_similarity_threshold: 0.4,
            enable_reranker: true,
            direct_system_prompt: "You are a helpful AI assistant. Answer the user's question directly.",
          };
        }
      }

      // --- Common processing logic for whatever settings were determined ---
      const processedSettings = { ...currentSettings };

      if (processedSettings.temperature !== undefined) {
        processedSettings.temperature = String(parseFloat(processedSettings.temperature).toFixed(1));
      } else {
        processedSettings.temperature = '0.0';
      }
      if (processedSettings.rag_mode === undefined) {
        processedSettings.rag_mode = 'similarity_filter';
      }
      if (typeof processedSettings.enable_grounding_check !== 'boolean') {
        processedSettings.enable_grounding_check = false;
      }
      if (typeof processedSettings.grounding_similarity_threshold !== 'number' || isNaN(processedSettings.grounding_similarity_threshold) || processedSettings.grounding_similarity_threshold < 0.0 || processedSettings.grounding_similarity_threshold > 1.0) {
        const convertedGST = parseFloat(processedSettings.grounding_similarity_threshold);
        processedSettings.grounding_similarity_threshold = (!isNaN(convertedGST) && convertedGST >= 0.0 && convertedGST <= 1.0) ? convertedGST : 0.4;
      }
      if (typeof processedSettings.enable_reranker !== 'boolean') {
        processedSettings.enable_reranker = true;
      }
      if (typeof processedSettings.direct_system_prompt !== 'string') {
        processedSettings.direct_system_prompt = '';
      }

      const numericFieldsDefaults = { top_p: 1.0, total_chunks: 50, answer_max_token: 512, top_k: 15, cosine_similarity_threshold: 0.3 };
      for (const field in numericFieldsDefaults) {
        if (processedSettings[field] !== undefined) {
          const numVal = parseFloat(processedSettings[field]);
          processedSettings[field] = isNaN(numVal) ? numericFieldsDefaults[field] : numVal;
        } else {
          processedSettings[field] = numericFieldsDefaults[field];
        }
      }

      setSettings(prev => ({ ...prev, ...processedSettings }));

      const initialProvider = findProviderForModel(processedSettings.llm_model);
      if (initialProvider) {
        setSelectedProvider(initialProvider);
      } else {
        // Do not override saved llm_model; wait for model catalog to load and map later
        if (!selectedProvider && providerOptions.length > 0) {
          setSelectedProvider(providerOptions[0]);
        }
      }
      setLoading(false);
    } // End of fetchSettingsForSession

    // Determine if we should fetch or just set defaults
    if (activeSessionId || userId) {
      fetchSettingsForSession();
    } else {
      // No session and no user context for fetching: load system defaults
      console.log("[UserSettingsSidebar] No userId or activeSessionId on mount. Initializing with system defaults.");
      let appDefaults;
      if (systemDefaults) {
        appDefaults = { ...systemDefaults };
        console.log("[UserSettingsSidebar] Using system defaults from database:", appDefaults);
      } else {
        // Fallback if system defaults not loaded yet
        console.warn("[UserSettingsSidebar] System defaults not loaded yet. Using hardcoded fallback.");
        appDefaults = {
          // embedding_model removed - BGE-M3 is fixed backend default
          system_prompt: "You are an AI assistant that provides helpful and concise answers based solely on the provided document and conversation history. Use the documents and the conversation history to understand the user's question and provide a relevant and accurate answer. Do not include verbatim quotes or references to previous messages or questions in your answer. DO NOT MENTION ANY PREVIOUS CHAT IN YOUR ANSWER. Do not repeat any previous chat in your answer. DO NOT USE ANY EXTERNAL KNOWLEDGE OR YOUR PRETRAINED INFORMATION. DO NOT USE ANY INFORMATION NOT PRESENT IN THE DOCUMENT. Focus on providing concise, relevant, clear and accurate responses from the context. If you cannot find the answer in the context or conversation history, state that the information is not available and do not offer additional assistance or ask follow-up questions.",
          llm_model: 'gpt-4o',
          temperature: 0.0,
          top_p: 1.0,
          total_chunks: 50,
          answer_max_token: 512,
          top_k: 15,
          cosine_similarity_threshold: 0.3,
          rag_mode: 'similarity_filter',
          enable_grounding_check: false,
          grounding_similarity_threshold: 0.4,
          enable_reranker: true,
          direct_system_prompt: "You are a helpful AI assistant. Answer the user's question directly.",
        };
      }
      // Make sure temperature is string for direct setting
      appDefaults.temperature = String(parseFloat(appDefaults.temperature).toFixed(1));
      setSettings(appDefaults);

      const defaultProvider = findProviderForModel(appDefaults.llm_model) || (providerOptions.length > 0 ? providerOptions[0] : '');
      setSelectedProvider(defaultProvider);
      // Adjust llm_model if the default isn't in the selected provider's list
      if (defaultProvider && llmModelsByProvider[defaultProvider]?.length > 0) {
        if (!llmModelsByProvider[defaultProvider].some(m => m.value === appDefaults.llm_model)) {
          setSettings(prev => ({ ...prev, llm_model: llmModelsByProvider[defaultProvider][0].value }));
        }
      } else if (defaultProvider) { // Provider exists but no models
        setSettings(prev => ({ ...prev, llm_model: '' }));
      }

      setLoading(false);
      setError('');
    }
  }, [userId, activeSessionId, systemDefaults, providerOptions, llmModelsByProvider]); // Updated dependencies

  // Handle change for general settings fields (embedding, system prompt, temp, etc.)
  const handleChange = (e, sliderValue) => { // Accept optional sliderValue
    const { name, value, type, checked } = e.target;

    // Determine the new value based on input type
    let newValue;
    if (name === 'grounding_similarity_threshold' || name === 'temperature') {
      // Special handling for Slider components
      // sliderValue is the direct number from the slider
      // e.target.value is the string representation if user types in input (not applicable here)
      newValue = sliderValue !== undefined ? sliderValue : parseFloat(value);
    } else if (type === 'checkbox' || checked !== undefined) {
      // Handle checkbox inputs AND Material-UI Switch components
      // Switch components don't have type='checkbox' but have 'checked' property
      newValue = checked;
    } else {
      newValue = value;
    }

    setSettings((prev) => ({
      ...prev,
      // Store temperature and grounding threshold as numbers, others as is
      [name]: (name === 'temperature' || name === 'grounding_similarity_threshold') ? Number(newValue) : newValue,
    }));
  };

  // NEW: Handle provider selection change
  const handleProviderChange = (e) => {
    const newProvider = e.target.value;
    setSelectedProvider(newProvider);

    // Reset the specific LLM model when provider changes
    const modelsForNewProvider = llmModelsByProvider[newProvider] || [];
    const firstModelValue = modelsForNewProvider.length > 0 ? modelsForNewProvider[0].value : '';
    setSettings(prev => ({
      ...prev,
      llm_model: firstModelValue // Set to first model of new provider or empty
    }));
  };

  // Handle specific LLM model selection change
  const handleModelChange = (e) => {
    const { name, value } = e.target; // name will be 'llm_model'
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!activeSessionId) {
      setError('No active session selected to save settings for.');
      console.error('[UserSettingsSidebar] handleSave called without activeSessionId.');
      return;
    }

    setLoading(true);
    setError('');
    setSaveSuccess('');
    console.log(`[UserSettingsSidebar] Attempting to save settings for activeSessionId: ${activeSessionId}`);

    // Prepare the payload for 'session_settings' table
    const settingsToSave = {
      ...settings, // Current settings from the UI state
      temperature: parseFloat(settings.temperature), // Ensure temperature is a number
      // IMPORTANT: Ensure session_id is the actual UUID of the active session
      session_id: activeSessionId
    };

    // Remove any fields that are not part of the session_settings table schema
    // or are not intended to be set directly (like primary keys if session_id is the conflict target)
    delete settingsToSave.id; // If 'id' was from an old user_settings structure
    delete settingsToSave.user_id; // user_id is implicit via chat_sessions link

    console.log(`[UserSettingsSidebar] Payload for session_settings upsert:`, settingsToSave);

    try {
      const { error: upsertError } = await supabase
        .from('session_settings') // CORRECT TABLE: 'session_settings'
        .upsert(settingsToSave, {
          onConflict: 'session_id' // CORRECT CONFLICT TARGET: 'session_id'
        });

      if (upsertError) {
        console.error('Error saving session settings to session_settings:', upsertError);
        setError(`Error saving settings: ${upsertError.message}`);
      } else {
        console.log(`[UserSettingsSidebar] Session settings saved successfully for session_id: ${activeSessionId}`);
        setSaveSuccess('Session settings saved successfully!');
        setOpenSnackbar(true);
        // Optionally, you might want to re-fetch settings here if the upsert doesn't return the full data
        // or if you want to ensure the state reflects any database-level changes (like updated_at)
        // fetchSettingsForSession(); // Be careful of infinite loops if not managed correctly
      }
    } catch (e) {
      console.error('Unexpected error during supabase upsert for session settings:', e);
      setError('An unexpected error occurred while saving settings.');
    } finally {
      setLoading(false); // Indicate saving operation has finished
      // Clear success message after a delay (optional)
      setTimeout(() => setSaveSuccess(''), 5000);
    }
  };


  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 336,
          boxSizing: 'border-box',
          backgroundColor: '#fff',
          borderRight: '1px solid #e0e0e0',
        }
      }}
    >
      <Box sx={{
        width: '100%', maxWidth: 336, p: 1.5, overflowY: 'auto', maxHeight: '100vh',
        '& .MuiTypography-root': { fontSize: '0.875rem' },
        '& .MuiFormLabel-root': { fontSize: '0.8rem' },
        '& .MuiInputBase-input': { fontSize: '0.875rem' },
        '& .MuiButton-root': { fontSize: '0.8rem', textTransform: 'none' }
      }}>
        <Typography variant="caption" gutterBottom sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
          Session Settings
        </Typography>
        {error && (
          <Typography variant="body2" color="error" gutterBottom>
            {error}
          </Typography>
        )}
        {/* Display the success message */}
        {saveSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {saveSuccess}
          </Alert>
        )}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setOpenSnackbar(false)} severity="success">
            {saveSuccess}
          </Alert>
        </Snackbar>

        {/* --- Integrations Section REMOVED (Back to Drawer) --- */}

        {/* Re-added: Embedding Model selector AT THE TOP */}
        <FormControl fullWidth margin="dense" size="small" disabled={loading}>
          <InputLabel id="embedding-model-label">Embedding Model</InputLabel>
          <Select
            labelId="embedding-model-label"
            label="Embedding Model"
            name="embedding_model"
            value={settings.embedding_model || ''}
            onChange={handleChange}
          >
            {embeddingModelOptions.length > 0 ? (
              embeddingModelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                Loading models...
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* System Prompt as a big text area */}
        <TextField
          fullWidth
          margin="dense"
          size="small"
          label="System Prompt"
          name="system_prompt"
          value={settings.system_prompt}
          onChange={handleChange}
          multiline
          rows={4}
          disabled={loading}
        />
        {/* --- NEW: Provider Selection --- */}
        <FormControl fullWidth margin="dense" size="small" disabled={loading}>
          <InputLabel id="provider-select-label">LLM Provider</InputLabel>
          <Select
            labelId="provider-select-label"
            id="provider-select"
            value={selectedProvider}
            label="LLM Provider"
            onChange={handleProviderChange} // Use specific handler
            size="small"
          >
            {providerOptions.map((provider) => (
              <MenuItem key={provider} value={provider}>
                {provider} {/* Display provider name */}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* --- NEW: Model Selection (depends on Provider) --- */}
        <FormControl fullWidth margin="dense" size="small" disabled={loading || !selectedProvider}>
          <InputLabel id="model-select-label">LLM Model</InputLabel>
          <Select
            labelId="model-select-label"
            id="model-select"
            name="llm_model" // Name matches the key in settings state
            value={settings.llm_model}
            label="LLM Model"
            onChange={handleModelChange} // Use specific handler for model
            size="small"
          >
            {availableModels.length === 0 && selectedProvider && (
              <MenuItem disabled value="">No models available for {selectedProvider}</MenuItem>
            )}
            {availableModels.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Temperature - Changed to Slider */}
        <Box sx={{ pl: 1, pr: 1, mt: 1 }}>
          <Typography gutterBottom variant="body2"> {/* Use body2 for main label */}
            Creativity (Temperature): {parseFloat(settings.temperature).toFixed(1)} {/* Display 1 decimal place */}
          </Typography>
          <Slider
            name="temperature"
            aria-label="Temperature"
            value={parseFloat(settings.temperature)} // Ensure value is number for Slider
            onChange={handleChange} // Pass event and value to handler
            step={0.1}
            marks
            min={0.0}
            max={2.0} // Standard range for temperature
            valueLabelDisplay="auto"
            sx={{
              height: 2,
              '& .MuiSlider-thumb': { width: 10, height: 10 },
              '& .MuiSlider-markLabel': { fontSize: '0.65rem' },
              '& .MuiSlider-valueLabel': { fontSize: '0.65rem' }
            }}
            disabled={loading}
          />
        </Box>
        {/* Top P 
        <TextField
          fullWidth
          margin="normal"
          label="Top P"
          name="top_p"
          value={settings.top_p}
          onChange={handleChange}
          type="number"
          inputProps={{ step: 0.1 }}
        />*/}
        {/* Total Chunks
        <TextField
          fullWidth
          margin="normal"
          label="Total Chunks"
          name="total_chunks"
          value={settings.total_chunks}
          onChange={handleChange}
          type="number"
        /> */}
        {/* Answer Max Token *
        <TextField
          fullWidth
          margin="normal"
          label="Answer Max Token"
          name="answer_max_token"
          value={settings.answer_max_token}
          onChange={handleChange}
          type="number"
        />/}
        {/* Top K 
        <TextField
          fullWidth
          margin="normal"
          label="Top K"
          name="top_k"
          value={settings.top_k}
          onChange={handleChange}
          type="number"
        />*/}
        {/* Cosine Similarity Threshold 
        <TextField
          fullWidth
          margin="normal"
          label="Cosine Similarity Threshold"
          name="cosine_similarity_threshold"
          value={settings.cosine_similarity_threshold}
          onChange={handleChange}
          type="number"
          inputProps={{ step: 0.1 }}
        /> */}
        {/* --- NEW: RAG Mode Selection --- */}
        <TextField
          select
          fullWidth
          margin="dense"
          size="small"
          label="RAG Strategy"
          name="rag_mode"
          value={settings.rag_mode}
          onChange={handleChange} // Use general handler
          disabled={loading}
          helperText="Similarity Filter is faster, Reranker may be more relevant."
        >
          <MenuItem key="similarity_filter" value="similarity_filter">
            Similarity Filter (Default)
          </MenuItem>
          <MenuItem key="reranker" value="reranker">
            Reranker (Slower, Potentially Better)
          </MenuItem>
          <MenuItem key="two_stage_summary" value="two_stage_summary">
            Two-Stage (Summaries First)
          </MenuItem>
        </TextField>
        {/* --- END RAG Mode Selection --- */}
        {/* --- NEW: Reranker Toggle --- */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={settings.enable_reranker}
              onChange={handleChange} // Use general handler
              name="enable_reranker"
              disabled={loading}
            />
          }
          label="Enable Reranker (Improves Relevance)"
          sx={{ mt: 2, display: 'block', '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
        <Typography variant="caption" display="block" gutterBottom sx={{ pl: 1 }}>
          Disabling may speed up responses but potentially reduce answer quality.
        </Typography>
        {/* --- END Reranker Toggle --- */}
        {/* --- NEW: Direct System Prompt --- */}
        <TextField
          fullWidth
          margin="dense"
          size="small"
          label="Direct Mode System Prompt"
          name="direct_system_prompt"
          value={(settings.direct_system_prompt && settings.direct_system_prompt.trim()) ? settings.direct_system_prompt : DEFAULT_DIRECT_SYSTEM_PROMPT}
          onChange={handleChange}
          multiline
          rows={3} // Adjust rows as needed
          disabled={loading}
          helperText="System prompt used when 'Direct Mode' is enabled in chat. Leave blank for default."
          sx={{ mt: 1 }}
        />
        {/* --- END Direct System Prompt --- */}
        {/* --- NEW: Grounding Check Setting --- */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={settings.enable_grounding_check}
              onChange={handleChange} // Use general handler
              name="enable_grounding_check"
              disabled={loading}
            />
          }
          label="Enable Answer Grounding Check"
          sx={{ mt: 1, display: 'block', '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} // Style as needed
        />
        <Typography variant="caption" display="block" gutterBottom sx={{ pl: 1 }}>
          Checks if the answer likely came from the context (adds slight delay).
        </Typography>
        {/* Conditionally render Slider only if grounding check is enabled */}
        {settings.enable_grounding_check && (
          <Box sx={{ pl: 1, pr: 1, mt: 1 }}>
            <Typography gutterBottom variant="caption">
              Grounding Threshold: {settings.grounding_similarity_threshold.toFixed(2)}
            </Typography>
            <Slider
              name="grounding_similarity_threshold"
              aria-label="Grounding Similarity Threshold"
              value={settings.grounding_similarity_threshold}
              onChange={handleChange} // Pass event and value to handler
              step={0.05}
              marks
              min={0.0}
              max={1.0}
              valueLabelDisplay="auto"
              sx={{
                height: 2,
                '& .MuiSlider-thumb': { width: 10, height: 10 },
                '& .MuiSlider-markLabel': { fontSize: '0.65rem' },
                '& .MuiSlider-valueLabel': { fontSize: '0.65rem' }
              }}
              disabled={loading}
            />
          </Box>
        )}
        {/* --- END Grounding Check Setting --- */}

        {/* --- NEW: FAQ Direct Answer Toggle --- */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={settings.faq_direct_answer || false}
              onChange={handleChange}
              name="faq_direct_answer"
              disabled={loading}
            />
          }
          label="FAQ Direct Answer (Instant)"
          sx={{ mt: 1, display: 'block', '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
        />
        <Typography variant="caption" display="block" gutterBottom sx={{ pl: 1 }}>
          Return FAQ answers instantly without LLM (~2s vs ~13s). Disable for more natural reformulation.
        </Typography>
        {/* --- END FAQ Direct Answer Toggle --- */}

        {/* Save Button */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading || !activeSessionId}
            size="small"
            sx={{ px: 1.5, py: 0.6 }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default React.memo(UserSettingsSidebar);
